import { DocumentData, QueryDocumentSnapshot, SnapshotOptions } from "firebase/firestore";
import { Job } from "./job";
import { Task } from "./task";
import { BaseData } from "./_global";
import { DRIVE_FOLDER_URL } from "@dis/gapi";
import { SPACE_URL } from "./chat";

export enum PROJECTSTATUS {
  // For Projects
  QUOTING = "QUOTING",
  QUOTED = "QUOTED",
  LOOKAHEAD = "LOOK AHEAD",
  PLANNING = "PLANNING",
  INPROGRESS = "IN PROGRESS",
  COMPLETE = "COMPLETE",
  DEAD = "DEAD",

  // For Leads
  NEW = "NEW",
  INBOUND = "INBOUND",
  CONTACTING = "CONTACTING",
  INTERESTED = "INTERESTED",
  QUOTING_PROCESS = "QUOTING PROCESS",
  AWARDED = "AWARDED",
  NOT_INTERESTED = "NOT INTERESTED",
  LOST = "LOST",
}

// Enums to be used for Data Connect properties
export enum PROJECT_TYPES {
  LEAD = "LEAD",
  MANUFACTURING = "MANUFACTURING",
  STAGING = "STAGING",
  PROJECT = "PROJECT",
  TEST = "TEST",
}

export const projectTypeOptions = Object.values(PROJECT_TYPES).map((type) => ({
  value: type,
  viewValue: type,
}));

export const leadStatusOptions = Object.values(PROJECTSTATUS).filter((status) => {
  return (
    status === PROJECTSTATUS.NEW ||
    status === PROJECTSTATUS.INBOUND ||
    status === PROJECTSTATUS.CONTACTING ||
    status === PROJECTSTATUS.INTERESTED ||
    status === PROJECTSTATUS.QUOTING_PROCESS ||
    status === PROJECTSTATUS.AWARDED ||
    status === PROJECTSTATUS.NOT_INTERESTED ||
    status === PROJECTSTATUS.LOST
  );
});

export const projectStatusOptions = Object.values(PROJECTSTATUS)
  .filter((status) => {
    return (
      status === PROJECTSTATUS.QUOTING ||
      status === PROJECTSTATUS.QUOTED ||
      status === PROJECTSTATUS.LOOKAHEAD ||
      status === PROJECTSTATUS.PLANNING ||
      status === PROJECTSTATUS.INPROGRESS ||
      status === PROJECTSTATUS.COMPLETE ||
      status === PROJECTSTATUS.DEAD
    );
  })
  .map((status) => ({
    value: status,
    viewValue: status,
  }));

export enum SPC_STATUS {
  NOT_NEEDED = "NOT NEEDED",
  NEEDS_SPC = "NEEDS SPC",
  BEING_WORKED_ON = "BEING WORKED ON",
  COMPLETED = "COMPLETED",
  SENT = "SENT",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  REWORKING = "REWORKING",
}

export const spcStatusOptions = Object.values(SPC_STATUS).map((status) => ({
  value: status,
  viewValue: status,
}));

export interface Project extends BaseData {
  projectId: string | null;
  projectNumber: string | null;
  customer: string | null;
  well: string | null;
  rig: string | null;
  location: string | null;
  network: string | null;
  ocsgLease: string | null;
  status: PROJECTSTATUS | null;
  notes: string | null;
  space: string | null;
  folderId: string | null;
  jobs: Job[];
  tasks: Task[];
  list: string | null;
  areaOfOperation: string | null;
  logId: string | null;
  sections: string | null;
  spudDate: Date | null;
  stagingBoolean: boolean | null;
  dcid: string | null;
  updatedBy: string;
  metadata: ProjectMetadata | null;
}

export class Project extends BaseData {
  constructor(data: Partial<Project>) {
    super(data);
    this.uid = data.projectNumber || null;
    this.projectId = data.projectId || null;
    this.projectNumber = data.projectNumber || null;
    this.folderId = data.folderId || null;
    this.customer = data.customer || null;
    this.network = data.network || null;
    this.ocsgLease = data.ocsgLease || null;
    this.location = data.location || null;
    this.well = data.well || null;
    this.rig = data.rig || null;
    this.status = data.status || null;
    this.space = data.space || null;
    this.notes = data.notes || null;
    this.jobs = data.jobs || [];
    this.tasks = data.tasks || [];
    this.list = data.list || null;
    this.areaOfOperation = data.areaOfOperation || null;
    this.logId = data.logId || null;
    this.sections = data.sections || null;
    this.spudDate = data.spudDate ? new Date(data.spudDate) : null;

    this.metadata = data.metadata || null;
    this.dcid = data.dcid || null;
    this.stagingBoolean = data.stagingBoolean || null;

    this.fromSpreadsheet(data);
  }

  public get bhaSections() {
    return this.sections?.split(",");
  }

  public get title() {
    return this.projectNumber;
  }

  public get splitProjectNumber() {
    return this.projectNumber?.split("-").slice(1).join("-");
  }

  public get spaceUrl() {
    if (this.space) {
      const spaceId = this.space.split("/")[1];
      return `${SPACE_URL}${spaceId}`;
    }
    return null;
  }

  public get folderUrl() {
    return `${DRIVE_FOLDER_URL}${this.folderId}`;
  }

  public get logUrl() {
    return `https://docs.google.com/document/d/${this.logId}`;
  }

  public get logUrlPreview() {
    return `https://docs.google.com/document/d/${this.logId}/preview`;
  }

  private fromSpreadsheet(project: Partial<Project>) {
    if (typeof project.metadata === "string") {
      this.metadata = JSON.parse(project.metadata);
    }
  }

  static toSpreadsheet(project: Project) {
    //@ts-expect-error
    project.metadata = JSON.stringify(project.metadata);
    // console.log(job);
    return project;
  }
}

export const projectConverter = {
  toFirestore(project: Project): DocumentData {
    return { ...project };
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): Project {
    const data = snapshot.data(options);
    return new Project(data);
  },
};

export interface ProjectMetadata {
  devBool: boolean;
  testingBool: boolean;
  stagedBool: boolean;
  spcCreatedBool: boolean;
  spcApprovedBool: boolean;
  flagged: boolean;
}
